import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { clientApi, setClientToken, errMsg } from "@/lib/api";
import { applyBrandColor } from "@/lib/theme";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, UserPlus, CalendarDays, Search } from "lucide-react";
import { lookupCep, maskCep } from "@/lib/viacep";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ClientRegister() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const slug = sp.get("slug") || localStorage.getItem("last_booking_slug") || "";
  const [form, setForm] = useState({
    name: "", email: "", password: "", phone: "", cpf_cnpj: "",
    cep: "", address_street: "", address_number: "", address_complement: "",
    neighborhood: "", city: "", state: "",
  });
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [brand, setBrand] = useState({});
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/api/public/${slug}/settings`).then(({ data }) => {
      setBrand(data || {});
      applyBrandColor(data?.brand_color || "");
    }).catch(() => {});
  }, [slug]);

  const doCepLookup = async () => {
    const digits = (form.cep || "").replace(/\D/g, "");
    if (digits.length !== 8) { toast.error("Informe um CEP com 8 dígitos"); return; }
    setCepLoading(true);
    try {
      const res = await lookupCep(digits);
      if (!res) { toast.error("CEP não encontrado"); return; }
      setForm((f) => ({
        ...f, cep: maskCep(digits),
        address_street: res.street || f.address_street,
        neighborhood: res.neighborhood || f.neighborhood,
        city: res.city || f.city,
        state: res.state || f.state,
      }));
      toast.success("Endereço preenchido");
    } finally { setCepLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const { data } = await clientApi.post("/client/register", form);
      setClientToken(data.token);
      localStorage.setItem("client_user", JSON.stringify(data.client));
      toast.success("Conta criada!");
      nav(slug ? `/agendar/${slug}` : "/agendar", { replace: true });
    } catch (err) { toast.error(errMsg(err, "Falha no cadastro")); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex items-center justify-center p-6">
      <form onSubmit={submit} className="surface p-8 max-w-md w-full space-y-4" data-testid="client-register-form">
        {brand.cover_url && (
          <div className="relative rounded-xl overflow-hidden -mx-2" data-testid="client-register-cover" style={{ aspectRatio: "3 / 1" }}>
            <img src={brand.cover_url} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <div className="absolute inset-0 bg-black/30" />
          </div>
        )}
        <div className="flex items-center gap-3 mb-2">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={brand.business_name || "Logo"} className="h-11 w-11 rounded-xl object-contain bg-white p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="h-11 w-11 rounded-xl bg-primary text-primary-foreground flex items-center justify-center"><CalendarDays className="h-5 w-5" /></div>
          )}
          <div>
            <h1 className="text-2xl font-display font-bold tracking-tight">Criar sua conta {brand.business_name ? `— ${brand.business_name}` : ""}</h1>
            <p className="text-xs text-muted-foreground">Para agendar rapidamente da próxima vez</p>
          </div>
        </div>
        <div className="space-y-2"><Label>Nome completo</Label><Input data-testid="cr-name" required className="h-11" value={form.name} onChange={set("name")} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2"><Label>Email</Label><Input data-testid="cr-email" type="email" required className="h-11" value={form.email} onChange={set("email")} /></div>
          <div className="space-y-2"><Label>Telefone</Label><Input data-testid="cr-phone" required className="h-11" value={form.phone} onChange={set("phone")} /></div>
        </div>
        <div className="space-y-2"><Label>Senha (mín. 6)</Label><Input data-testid="cr-password" type="password" minLength={6} required className="h-11" value={form.password} onChange={set("password")} /></div>
        <div className="space-y-2"><Label>CPF/CNPJ (para cobrança e NF-e)</Label><Input data-testid="cr-cpf" className="h-11" value={form.cpf_cnpj} onChange={set("cpf_cnpj")} /></div>

        {/* Endereço estruturado — enviado ao Asaas para emissão de NF-e. */}
        <div className="pt-2 border-t border-border">
          <div className="text-sm font-medium mb-2">Endereço (opcional, agiliza emissão de NF-e)</div>
          <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
            <div className="space-y-2">
              <Label>CEP</Label>
              <Input
                data-testid="cr-cep"
                value={form.cep}
                onChange={(e) => setForm({ ...form, cep: maskCep(e.target.value) })}
                onBlur={() => { if ((form.cep || "").replace(/\D/g, "").length === 8) doCepLookup(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); doCepLookup(); } }}
                placeholder="00000-000"
                inputMode="numeric"
                className="h-11"
              />
            </div>
            <Button type="button" variant="outline" onClick={doCepLookup} disabled={cepLoading} data-testid="btn-cr-cep-lookup" className="h-11">
              {cepLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-1" /> Buscar</>}
            </Button>
          </div>
          <div className="grid grid-cols-[1fr_100px] gap-3 mt-3">
            <div className="space-y-2"><Label>Rua/Logradouro</Label><Input data-testid="cr-street" className="h-11" value={form.address_street} onChange={set("address_street")} /></div>
            <div className="space-y-2"><Label>Número</Label><Input data-testid="cr-num" className="h-11" value={form.address_number} onChange={set("address_number")} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div className="space-y-2"><Label>Complemento</Label><Input data-testid="cr-comp" className="h-11" value={form.address_complement} onChange={set("address_complement")} /></div>
            <div className="space-y-2"><Label>Bairro</Label><Input data-testid="cr-neigh" className="h-11" value={form.neighborhood} onChange={set("neighborhood")} /></div>
          </div>
          <div className="grid grid-cols-[1fr_90px] gap-3 mt-3">
            <div className="space-y-2"><Label>Cidade</Label><Input data-testid="cr-city" className="h-11" value={form.city} onChange={set("city")} /></div>
            <div className="space-y-2"><Label>UF</Label><Input data-testid="cr-state" maxLength={2} className="h-11" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} /></div>
          </div>
        </div>

        <Button data-testid="btn-cr-submit" disabled={loading} className="w-full h-12">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />} Criar conta
        </Button>
        <div className="text-center text-sm text-muted-foreground">
          Já tem conta? <Link to={slug ? `/agendar/login?slug=${slug}` : "/agendar/login"} className="underline text-foreground" data-testid="link-cl-login">Entrar</Link>
        </div>
      </form>
    </div>
  );
}
